import { onBeforeUnmount, onMounted } from 'vue';

const VISITOR_COOKIE = 'wl_lp_vid';
const SESSION_COOKIE = 'wl_lp_sid';
const CTA_SELECTOR = '[data-lp-cta]';
const MAX_ENGAGE_SECONDS = 1800;

const readCookie = name => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
};

const writeCookie = (name, value, days) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${days * 24 * 3600}; SameSite=Lax${secure}`;
};

const persistentId = (name, storage, days) => {
  let id = readCookie(name) || storage.getItem(name) || '';
  if (!id) {
    id = crypto.randomUUID();
    writeCookie(name, id, days);
    storage.setItem(name, id);
  }
  return id;
};

const scrollPercent = () => {
  const { scrollHeight, clientHeight } = document.documentElement;
  const max = scrollHeight - clientHeight;
  if (max <= 0) return 100;
  return Math.min(100, Math.max(0, Math.round((window.scrollY / max) * 100)));
};

// Port do LpTracker do Papo-app: page_view, engage (tempo + scroll), cta e click.
export function useLandingTracker(landing) {
  if (!landing.eventsPath) return;

  const visitorId = persistentId(VISITOR_COOKIE, localStorage, 365);
  const sessionId = persistentId(SESSION_COOKIE, sessionStorage, 1);
  const query = new URLSearchParams(window.location.search);
  const context = {
    version_id: landing.version.id,
    visitor_id: visitorId,
    session_id: sessionId,
    locale: landing.locale,
    path: window.location.pathname,
    utm_source: query.get('utm_source') || undefined,
    utm_medium: query.get('utm_medium') || undefined,
    utm_campaign: query.get('utm_campaign') || undefined,
    gclid: query.get('gclid') || undefined,
    source: query.get('utm_source') || query.get('gclid') ? 'paid' : 'direct',
    referrer: document.referrer || undefined,
    ...Object.fromEntries(
      ['utm_content', 'utm_term', 'fbclid', 'msclkid', 'ttclid'].map(key => [
        key,
        query.get(key) || undefined,
      ])
    ),
  };

  const track = (eventName, properties = {}) =>
    fetch(landing.eventsPath, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...context, event_name: eventName, properties }),
      keepalive: true,
    }).catch(() => {});

  let startedAt = 0;
  let maxScroll = 0;
  let engageSent = false;

  const onScroll = () => {
    maxScroll = Math.max(maxScroll, scrollPercent());
  };

  const sendEngage = () => {
    if (engageSent) return;
    const seconds = Math.round((Date.now() - startedAt) / 1000);
    if (seconds < 1 && maxScroll < 5) return;
    engageSent = true;
    track('engage', {
      seconds: Math.min(seconds, MAX_ENGAGE_SECONDS),
      scrollMax: maxScroll,
    });
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') sendEngage();
  };

  const onClick = event => {
    const cta = event.target.closest(CTA_SELECTOR);
    if (cta) {
      track('cta', {
        href: cta.getAttribute('href') || cta.dataset.lpCta || '',
        text: cta.textContent.trim().slice(0, 120),
      });
      return;
    }

    const link = event.target.closest('a');
    const href = link?.getAttribute('href') || '';
    if (!href.startsWith('http') && !href.startsWith('/')) return;
    track('click', { href, text: link.textContent.trim().slice(0, 80) });
  };

  onMounted(() => {
    startedAt = Date.now();
    maxScroll = scrollPercent();
    track('page_view');
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', sendEngage);
    document.addEventListener('visibilitychange', onVisibility);
    document.addEventListener('click', onClick, true);
  });

  onBeforeUnmount(() => {
    sendEngage();
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('pagehide', sendEngage);
    document.removeEventListener('visibilitychange', onVisibility);
    document.removeEventListener('click', onClick, true);
  });
}
